import React from 'react';
import Icon from '@mui/material/Icon';
import Popover from '@mui/material/Popover';
import MenuItem from '@mui/material/MenuItem';
import ButtonBase from '@mui/material/ButtonBase';
import IconButton from '@mui/material/IconButton';
import Badge from '@mui/material/Badge';
import Drawer from '@mui/material/Drawer';
import Divider from '@mui/material/Divider';
import { alpha, useTheme as useMuiTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import Menu from '@mui/material/Menu';
import MDBox from 'components/MDBox';
import MDTypography from 'components/MDTypography';
import MDInput from 'components/MDInput';
import MDButton from 'components/MDButton';
import MDSelect from 'components/MDSelect';

/* ---------------- small atoms ---------------- */
function TinyPill({ icon, label }) {
  const theme = useMuiTheme();
  return (
    <MDBox
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 0.6,
        px: 1,
        py: 0.55,
        borderRadius: '999px',
        border: `1px solid ${theme.palette.divider}`,
        background: theme.palette.background.paper,
        fontSize: 12,
        lineHeight: 1,
      }}
    >
      <Icon sx={{ fontSize: 16, opacity: 0.75 }}>{icon}</Icon>
      <MDTypography variant="caption" fontWeight="bold" sx={{ opacity: 0.9 }}>
        {label}
      </MDTypography>
    </MDBox>
  );
}

function SegButton({ active, icon, label, tone = 'neutral', onClick }) {
  const theme = useMuiTheme();

  const tones = {
    neutral: {
      main: theme.palette.text.secondary,
      bg: theme.palette.background.paper,
      border: theme.palette.divider,
      softBg: theme.palette.action.hover,
    },
    success: {
      main: theme.palette.success.main,
      bg: alpha(theme.palette.success.main, 0.14),
      border: alpha(theme.palette.success.main, 0.45),
      softBg: alpha(theme.palette.success.main, 0.08),
    },
    error: {
      main: theme.palette.error.main,
      bg: alpha(theme.palette.error.main, 0.14),
      border: alpha(theme.palette.error.main, 0.45),
      softBg: alpha(theme.palette.error.main, 0.08),
    },
    warning: {
      main: theme.palette.warning.main,
      bg: alpha(theme.palette.warning.main, 0.18),
      border: alpha(theme.palette.warning.main, 0.55),
      softBg: alpha(theme.palette.warning.main, 0.1),
    },
    ice: {
      main: theme.palette.info.main,
      bg: alpha(theme.palette.info.main, 0.16),
      border: alpha(theme.palette.info.main, 0.55),
      softBg: alpha(theme.palette.info.main, 0.09),
    },
  };

  const t = tones[tone] || tones.neutral;

  return (
    <ButtonBase
      onClick={onClick}
      sx={{
        px: 1.1,
        py: 0.9,
        borderRadius: '12px',
        border: `1px solid ${active ? t.border : theme.palette.divider}`,
        background: active ? t.bg : t.softBg,
        display: 'inline-flex',
        alignItems: 'center',
        gap: 0.6,
        transition: 'all .15s ease',
        '@media (hover: hover)': {
          '&:hover': { transform: 'translateY(-1px)' },
        },
      }}
    >
      <Icon
        sx={{
          fontSize: 18,
          color: t.main,
          opacity: active ? 1 : 0.75,
        }}
      >
        {icon}
      </Icon>

      <MDTypography
        variant="caption"
        fontWeight="bold"
        sx={{ color: active ? t.main : theme.palette.text.secondary }}
      >
        {label}
      </MDTypography>
    </ButtonBase>
  );
}

/* ---------------- main ---------------- */
export function MembersTableHeaderBar({
  title,
  q,
  onQ,
  onCreate,
  onRefresh,
  count,
  filters,
  onChangeFilters,
  trainerOptions = [],
  planOptions = [],
  sortBy,
  onSortBy,
}) {
  const theme = useMuiTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const mIconSx = {
    width: 44,
    height: 44,
    borderRadius: '14px',
    border: `1px solid ${theme.palette.divider}`,
    background: theme.palette.background.paper,
  };
  // ✅ (A) appliedCount hesap
  const appliedCount = React.useMemo(() => {
    let c = 0;
    if (filters?.trainerId && filters.trainerId !== 'ALL') c++;
    if (filters?.planName) c++;
    if (filters?.paymentStatus && filters.paymentStatus !== 'ALL') c++;
    if (filters?.status && filters.status !== 'ALL') c++;
    return c;
  }, [filters]);

  // ✅ (B) desktop popover anchor
  const [anchorEl, setAnchorEl] = React.useState(null);
  const [sortEl, setSortEl] = React.useState(null);
  const sortOpen = Boolean(sortEl);

  const sortLabel =
    sortBy === 'DEBT_DESC'
      ? 'Borç (Çok→Az)'
      : sortBy === 'DEBT_ASC'
      ? 'Borç (Az→Çok)'
      : sortBy === 'NAME_ASC'
      ? 'İsim (A→Z)'
      : sortBy === 'CREATED_DESC'
      ? 'Kayıt (Yeni→Eski)'
      : 'Sırala';
  const popOpen = Boolean(anchorEl);

  // ✅ (C) mobile drawer open
  const [drawerOpen, setDrawerOpen] = React.useState(false);

  // ✅ (D) mobile draft filters (mobilde “Uygula” ile commit)
  const [draft, setDraft] = React.useState(filters || {});
  React.useEffect(() => {
    setDraft(filters || {});
  }, [filters, drawerOpen]); // drawer açılırken sync

  const setFDesktop = (patch) => {
    const next = { ...(filters || {}), ...(patch || {}) };
    onChangeFilters?.(next);
  };

  const setFDraft = (patch) => {
    setDraft((prev) => ({ ...(prev || {}), ...(patch || {}) }));
  };

  const resetValue = {
    trainerId: 'ALL',
    planName: '',
    paymentStatus: 'ALL',
    status: 'ALL',
  };

  const resetDesktop = () => onChangeFilters?.(resetValue);

  const resetMobile = () => setDraft(resetValue);

  const applyMobile = () => {
    onChangeFilters?.(draft);
    setDrawerOpen(false);
  };

  // ✅ (E) shared “Filter Content” renderer
  const FilterContent = ({ mode }) => {
    const isDraft = mode === 'draft';
    const cur = isDraft ? draft : filters;

    const setF = isDraft ? setFDraft : setFDesktop;

    return (
      <MDBox sx={{ p: 0.5, display: 'grid', gap: 0.85 }}>
        {/* Trainer */}
        <MDBox>
          <MDTypography variant="caption" fontWeight="bold" sx={{ opacity: 0.85 }}>
            Eğitmen
          </MDTypography>
          <MDSelect
            value={cur?.trainerId ?? 'ALL'}
            onChange={(e) => setF({ trainerId: e.target.value })}
            sx={{ mt: 0.6, '& .MuiInputBase-root': { borderRadius: '14px', height: 44 } }}
          >
            <MenuItem value="ALL">Tümü</MenuItem>
            {(trainerOptions || []).map((t) => (
              <MenuItem key={t.id} value={t.id}>
                {t.label}
              </MenuItem>
            ))}
          </MDSelect>
        </MDBox>

        {/* Plan */}
        <MDBox>
          <MDTypography variant="caption" fontWeight="bold" sx={{ opacity: 0.85 }}>
            Paket
          </MDTypography>
          <MDSelect
            value={cur?.planName ?? ''}
            onChange={(e) => setF({ planName: e.target.value })}
            sx={{ mt: 0.6, '& .MuiInputBase-root': { borderRadius: '14px', height: 44 } }}
          >
            <MenuItem value="">Hepsi</MenuItem>
            {(planOptions || []).map((p) => (
              <MenuItem key={p} value={p}>
                {p}
              </MenuItem>
            ))}
          </MDSelect>
        </MDBox>

        {/* Payment Status */}
        <MDBox>
          <MDTypography variant="caption" fontWeight="bold" sx={{ opacity: 0.85 }}>
            Ödeme durumu
          </MDTypography>

          <MDBox sx={{ mt: 0.6, display: 'flex', gap: 0.8, flexWrap: 'wrap' }}>
            <SegButton
              active={(cur?.paymentStatus ?? 'ALL') === 'ALL'}
              icon="all_inclusive"
              label="Hepsi"
              tone="neutral"
              onClick={() => setF({ paymentStatus: 'ALL' })}
            />
            <SegButton
              active={(cur?.paymentStatus ?? 'ALL') === 'DEBT'}
              icon="report"
              label="Ödenmedi"
              tone="error"
              onClick={() => setF({ paymentStatus: 'DEBT' })}
            />
            <SegButton
              active={(cur?.paymentStatus ?? 'ALL') === 'NO_DEBT'}
              icon="task_alt"
              label="Ödendi"
              tone="success"
              onClick={() => setF({ paymentStatus: 'NO_DEBT' })}
            />
          </MDBox>
        </MDBox>

        {/* Membership status */}
        <MDBox>
          <MDTypography variant="caption" fontWeight="bold" sx={{ opacity: 0.85 }}>
            Üyelik durumu
          </MDTypography>

          <MDBox sx={{ mt: 0.6, display: 'flex', gap: 0.8, flexWrap: 'wrap' }}>
            <SegButton
              active={(cur?.status ?? 'ALL') === 'ALL'}
              icon="layers"
              label="Hepsi"
              tone="neutral"
              onClick={() => setF({ status: 'ALL' })}
            />
            <SegButton
              active={(cur?.status ?? 'ALL') === 'ACTIVE'}
              icon="check_circle"
              label="Aktif"
              tone="success"
              onClick={() => setF({ status: 'ACTIVE' })}
            />
            <SegButton
              active={(cur?.status ?? 'ALL') === 'PASSIVE'}
              icon="cancel"
              label="Pasif"
              tone="warning"
              onClick={() => setF({ status: 'PASSIVE' })}
            />
            <SegButton
              active={(cur?.status ?? 'ALL') === 'FROZEN'}
              icon="ac_unit"
              label="Dondur"
              tone="ice"
              onClick={() => setF({ status: 'FROZEN' })}
            />
          </MDBox>
        </MDBox>
      </MDBox>
    );
  };

  /* ---------------- render ---------------- */
  return (
    <MDBox
      sx={{
        p: 1.5,
        background: `linear-gradient(135deg, ${alpha(theme.palette.info.main, 0.12)}, ${
          theme.palette.background.paper
        })`,
        display: 'grid',
        gap: 1,
        // ✅ (1) Mobil layout: 2 satır
        gridTemplateColumns: isMobile ? '1fr' : '260px 1fr auto auto auto',
        alignItems: 'center',
      }}
    >
      {/* ✅ (2) Title + pills */}
      <MDBox display="flex" alignItems="center" gap={1} sx={{ minWidth: 0 }}>
        <MDBox
          sx={{
            width: 38,
            height: 38,
            borderRadius: '14px',
            display: 'grid',
            placeItems: 'center',
            border: `1px solid ${theme.palette.divider}`,
            background: theme.palette.background.paper,
            flex: '0 0 auto',
          }}
        >
          <Icon sx={{ opacity: 0.85 }}>groups</Icon>
        </MDBox>

        <MDBox sx={{ minWidth: 0 }}>
          <MDTypography variant="h6" fontWeight="bold" sx={{ lineHeight: 1.1 }}>
            {title}
          </MDTypography>
          <MDBox display="flex" gap={0.8} sx={{ mt: 0.4, flexWrap: 'wrap' }}>
            <TinyPill label={`Görünen: ${count ?? 0}`} />
            {appliedCount ? <TinyPill icon="filter_alt" label={`${appliedCount} filtre`} /> : null}
          </MDBox>
        </MDBox>
      </MDBox>

      {/* ✅ (3) Search + actions row (mobilde aynı satır içinde) */}
      <MDBox
        sx={{
          display: 'grid',
          gap: 1,
          gridTemplateColumns: isMobile ? '1fr auto auto auto' : '1fr',
          alignItems: 'center',
        }}
      >
        <MDInput
          value={q}
          onChange={(e) => onQ?.(e.target.value)}
          placeholder="Ara: isim, telefon, paket, Eğitmen…"
          InputProps={{
            startAdornment: (
              <MDBox sx={{ display: 'flex', alignItems: 'center', pr: 1 }}>
                <Icon sx={{ opacity: 0.7, fontSize: 18 }}>search</Icon>
              </MDBox>
            ),
          }}
          sx={{
            '& .MuiInputBase-root': {
              height: isMobile ? 44 : 40,
              borderRadius: '14px',
            },
            '& input': { py: 0.5, fontSize: 14 },
          }}
        />

        {/* ✅ mobil filtre ikon */}
        {isMobile ? (
          <IconButton onClick={() => setDrawerOpen(true)} sx={mIconSx}>
            <Badge
              badgeContent={appliedCount}
              color="error"
              overlap="circular"
              invisible={!appliedCount}
            >
              <Icon sx={{ fontSize: 20, opacity: 0.85 }}>tune</Icon>
            </Badge>
          </IconButton>
        ) : null}
        {isMobile ? (
          <IconButton onClick={(e) => setSortEl(e.currentTarget)} sx={mIconSx}>
            <Icon sx={{ fontSize: 20, opacity: 0.85 }}>sort</Icon>
          </IconButton>
        ) : null}
        {/* ✅ mobil yeni ikon */}
        {isMobile ? (
          <IconButton
            onClick={() => onCreate?.()}
            sx={{
              ...mIconSx,
              border: `1px solid ${alpha(theme.palette.success.main, 0.35)}`,
              background: alpha(theme.palette.success.main, 0.12),
            }}
          >
            <Icon sx={{ fontSize: 20, color: theme.palette.success.main }}>person_add</Icon>
          </IconButton>
        ) : null}
      </MDBox>

      {/* ✅ desktop actions (filtre + yeni) */}
      {!isMobile ? (
        <>
          <MDButton
            variant="outlined"
            color="dark"
            sx={{ borderRadius: '14px', minHeight: 40, justifySelf: 'end' }}
            onClick={(e) => setAnchorEl(e.currentTarget)}
          >
            <Icon sx={{ mr: 0.5, fontSize: 18 }}>
              {appliedCount ? 'filter_alt' : 'filter_alt_off'}
            </Icon>
            Filtre
          </MDButton>
          <MDButton
            variant="outlined"
            color="dark"
            sx={{ borderRadius: '14px', minHeight: 40, justifySelf: 'end' }}
            onClick={(e) => setSortEl(e.currentTarget)}
          >
            <Icon sx={{ mr: 0.5, fontSize: 18 }}>sort</Icon>
            {sortLabel}
          </MDButton>
          <MDButton
            variant="gradient"
            color="success"
            sx={{ borderRadius: '14px', minHeight: 40, justifySelf: 'end' }}
            onClick={() => onCreate?.()}
          >
            <Icon sx={{ mr: 0.5, fontSize: 18 }}>person_add</Icon>
            Yeni
          </MDButton>
        </>
      ) : null}

      {/* ✅ desktop popover */}
      {!isMobile ? (
        <Popover
          open={popOpen}
          anchorEl={anchorEl}
          onClose={() => setAnchorEl(null)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          slotProps={{ backdrop: { invisible: true } }}
          PaperProps={{
            sx: {
              mt: 1,
              borderRadius: '18px',
              border: `1px solid ${theme.palette.divider}`,
              boxShadow: theme.shadows[10],
              overflow: 'hidden',
              width: 360,
              maxWidth: '92vw',
              backgroundColor: `${theme.palette.background.paper} !important`,
            },
          }}
        >
          {/* header */}
          <MDBox
            sx={{
              p: 1.6,
              background: `linear-gradient(180deg, ${alpha(theme.palette.info.main, 0.14)}, ${alpha(
                theme.palette.info.main,
                0.04
              )})`,
              borderBottom: `1px solid ${theme.palette.divider}`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 1,
            }}
          >
            <MDBox sx={{ display: 'flex', alignItems: 'center', gap: 1.1 }}>
              <MDBox
                sx={{
                  width: 38,
                  height: 38,
                  borderRadius: '14px',
                  display: 'grid',
                  placeItems: 'center',
                  backgroundColor: theme.palette.background.paper,
                  border: `1px solid ${theme.palette.divider}`,
                  boxShadow: theme.shadows[2],
                }}
              >
                <Icon sx={{ fontSize: 18, color: theme.palette.info.main }}>tune</Icon>
              </MDBox>
              <MDBox>
                <MDTypography variant="button" fontWeight="bold">
                  Filtreler
                </MDTypography>
              </MDBox>
            </MDBox>

            <MDBox sx={{ display: 'flex', gap: 0.6 }}>
              <MDButton
                variant="outlined"
                color="dark"
                size="small"
                onClick={resetDesktop}
                sx={{ borderRadius: '12px', minHeight: 36, px: 1.2 }}
              >
                <Icon sx={{ mr: 0.6, fontSize: 18 }}>restart_alt</Icon>
                Sıfırla
              </MDButton>

              <IconButton onClick={() => setAnchorEl(null)}>
                <Icon>close</Icon>
              </IconButton>
            </MDBox>
          </MDBox>

          <MDBox sx={{ maxHeight: '60vh', overflow: 'auto' }}>
            <FilterContent mode="live" />
          </MDBox>
        </Popover>
      ) : null}

      {/* ✅ mobile drawer */}
      {isMobile ? (
        <Drawer
          anchor="bottom"
          open={drawerOpen}
          onClose={() => setDrawerOpen(false)}
          PaperProps={{
            sx: {
              borderTopLeftRadius: '22px',
              borderTopRightRadius: '22px',
              border: `1px solid ${theme.palette.divider}`,
              overflow: 'hidden',
            },
          }}
        >
          {/* drawer header */}
          <MDBox
            sx={{ p: 1.6, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}
          >
            <MDBox sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <MDBox
                sx={{
                  width: 38,
                  height: 38,
                  borderRadius: '14px',
                  display: 'grid',
                  placeItems: 'center',
                  background: alpha(theme.palette.info.main, 0.12),
                  border: `1px solid ${alpha(theme.palette.info.main, 0.22)}`,
                }}
              >
                <Icon sx={{ fontSize: 18, color: theme.palette.info.main }}>tune</Icon>
              </MDBox>
              <MDBox>
                <MDTypography variant="button" fontWeight="bold">
                  Filtreler
                </MDTypography>
                <MDTypography variant="caption" sx={{ color: theme.palette.text.secondary }}>
                  Seç → Uygula
                </MDTypography>
              </MDBox>
            </MDBox>

            <IconButton onClick={() => setDrawerOpen(false)}>
              <Icon>close</Icon>
            </IconButton>
          </MDBox>

          <Divider />

          <MDBox sx={{ px: 1.5, py: 1.2, maxHeight: '70vh', overflow: 'auto' }}>
            <FilterContent mode="draft" />
          </MDBox>

          <Divider />

          {/* drawer footer actions */}
          <MDBox sx={{ p: 1.5, display: 'flex', gap: 1 }}>
            <MDButton
              variant="outlined"
              color="dark"
              onClick={resetMobile}
              sx={{ borderRadius: '14px', height: 44, flex: 1 }}
            >
              Sıfırla
            </MDButton>
            <MDButton
              variant="gradient"
              color="info"
              onClick={applyMobile}
              sx={{ borderRadius: '14px', height: 44, flex: 1, fontWeight: 900 }}
            >
              Uygula
            </MDButton>
          </MDBox>
        </Drawer>
      ) : null}
      <Menu
        anchorEl={sortEl}
        open={sortOpen}
        onClose={() => setSortEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <MenuItem
          selected={sortBy === 'DEBT_DESC'}
          onClick={() => {
            onSortBy?.('DEBT_DESC');
            setSortEl(null);
          }}
        >
          Borç (Çok → Az)
        </MenuItem>

        <MenuItem
          selected={sortBy === 'DEBT_ASC'}
          onClick={() => {
            onSortBy?.('DEBT_ASC');
            setSortEl(null);
          }}
        >
          Borç (Az → Çok)
        </MenuItem>

        <MenuItem
          selected={sortBy === 'NAME_ASC'}
          onClick={() => {
            onSortBy?.('NAME_ASC');
            setSortEl(null);
          }}
        >
          İsim (A → Z)
        </MenuItem>

        <MenuItem
          selected={sortBy === 'CREATED_DESC'}
          onClick={() => {
            onSortBy?.('CREATED_DESC');
            setSortEl(null);
          }}
        >
          Kayıt (Yeni → Eski)
        </MenuItem>
      </Menu>
    </MDBox>
  );
}
